import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ShieldCheck, FileText, Download, Lock, AlertTriangle } from 'lucide-react'
import api from '../services/api'

export default function SharedItemPage() {
    const { token } = useParams()
    const[data, setData] = useState(null)
    const [error, setError] = useState(null)
    const[loading, setLoading] = useState(true)

    useEffect(() => {
        api.get(`/api/share/${token}`)
            .then(res => setData(res.data))
            .catch(err => setError(err.response?.data?.detail || "Ce lien n'est plus valide."))
            .finally(() => setLoading(false))
    }, [token])

    const handleDownload = async () => {
        try {
            const response = await api.get(`/api/share/${token}/download`, { responseType: 'blob' })
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `${data.name}.pdf`)
            document.body.appendChild(link)
            link.click()
            link.remove()
        } catch (error) {
            alert("Erreur lors du téléchargement")
        }
    }

    if (loading) return <div className="min-h-screen flex justify-center items-center bg-gray-50"><div className="spinner border-sage-600" /></div>

    if (error) return (
        <div className="min-h-screen flex justify-center items-center bg-gray-50 p-4">
            <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center border border-red-100">
                <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4 opacity-50" />
                <h1 className="text-xl font-bold text-gray-800 mb-2">Accès refusé</h1>
                <p className="text-gray-500">{error}</p>
                <p className="text-xs text-gray-400 mt-6 pt-4 border-t border-gray-100">Par mesure de sécurité, les liens expirent après 7 jours.</p>
            </div>
        </div>
    )

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4">
            <div className="max-w-3xl mx-auto">
                
                {/* Header Sécurisé */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 shadow-inner">
                        <ShieldCheck className="w-8 h-8 text-blue-600" />
                    </div>
                    <h1 className="text-2xl font-serif text-gray-800">Espace Médical Partagé</h1>
                    <p className="text-gray-500 mt-2 flex items-center justify-center gap-2">
                        <Lock className="w-3 h-3" /> Chiffré de bout en bout
                    </p>
                </div>

                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    {/* Infos Document */}
                    <div className="p-6 bg-sage-50/50 border-b border-gray-100 flex justify-between items-center">
                        <div>
                            <p className="text-xs font-bold text-sage-600 uppercase mb-1">Dossier Patient</p>
                            <h2 className="text-xl font-bold text-gray-800">{data.patient}</h2>
                            <p className="text-sm text-gray-500 mt-1">Généré le {new Date(data.date).toLocaleDateString('fr-FR')}</p>
                        </div>
                        {data.type === 'document' && (
                            <button onClick={handleDownload} className="btn bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 shadow-md">
                                <Download className="w-4 h-4" /> Télécharger (PDF)
                            </button>
                        )}
                    </div>

                    {/* Contenu (Si c'est une note texte, on l'affiche directement) */}
                    {data.type === 'note' ? (
                        <div className="p-8">
                            <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2 border-b border-gray-100 pb-2">
                                <FileText className="w-5 h-5 text-sage-500" /> Compte-Rendu Clinique
                            </h3>
                            <div className="text-gray-700 whitespace-pre-wrap font-sans leading-relaxed text-sm">
                                {data.content}
                            </div>
                        </div>
                    ) : (
                        <div className="p-12 text-center text-gray-500 bg-gray-50">
                            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-lg font-medium text-gray-700 mb-2">{data.name}</p>
                            <p className="text-sm mb-6">Ce document PDF est prêt à être consulté.</p>
                            <button onClick={handleDownload} className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-100 shadow-sm">
                                Télécharger le fichier
                            </button>
                        </div>
                    )}
                </div>

               {/* Footer Légal */}
                <p className="text-center text-xs text-gray-400 mt-8">
                    Ce document vous est transmis par <strong>{data.sender || 'un professionnel de santé'}</strong>.<br/>
                    Il est soumis au secret médical et ne doit pas être partagé.
                </p>
            </div>
        </div>
    )
}